import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useIsMutating } from 'react-query';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { usePersonalInfo } from './usePersonalInfo';
import { PersonalInfoLoading } from './PersonalInfo.loading';
import { PersonalInfoDTO } from './api/usePersonalInfoQuery';
import { TrustsDTO } from './api/useTrustsQuery';
import {
  UPDATE_PERSONAL_INFO_MK,
  usePersonalInfoUpdateMutation,
} from './api/useUpdatePersonalInfoMutation';
import {
  dataToForm,
  FORM_ID,
  FormValues,
  PersonalInfoContext,
  resetForm,
  validation,
} from './PersonalInfo.meta';
import { showOPLConfirmationDialog } from '../../helpers/showOPLConfirmationDialog';
import { Alert } from '../../CommonComponents/OPL/Alert/Alert';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { DeletePartnerModal } from './components/PartnerInfo/components/DeletePartnerModal/DeletePartnerModal';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { Wrapper, WrapperVariant } from './components/Wrapper/Wrapper';
import { PageId } from '../../Workflow/api/useWorkflowDataQuery';
import {
  EstatePlanningDTO,
  EstatePlanningOptionsDTO,
} from './api/useEstatePlanningPersonalInfoQuery';
import { FormWrapper } from './components/FormWrapper/FormWrapper';
import {
  UPDATE_ESTATE_PLANNING_PERSONAL_INFO_MK,
  useEstatePlanningPersonalInfoMutation,
} from './api/useEstatePlanningPersonalInfoMutation';
import { formToBodyPersonalInfo } from './helpers/formToBodyPersonalInfo';
import { formToBodyEstatePlanning } from './helpers/formToBodyEstatePlanning';

interface Props {
  workflowProps?: WorkflowComponentProps;
}

export const PersonalInfo: React.FC<Props> = ({ workflowProps }) => {
  const showEstatePlanning = workflowProps?.pageId === PageId.EstatePlanningPersonalInfo;

  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const { isLoading, isError } = usePersonalInfo({ showEstatePlanning });

  const isUpdatingPersonalInfo = useIsMutating({ mutationKey: UPDATE_PERSONAL_INFO_MK }) > 0;

  const isUpdatingEstatePlanningInfo =
    useIsMutating({ mutationKey: UPDATE_ESTATE_PLANNING_PERSONAL_INFO_MK }) > 0;

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper
        variant={
          showEstatePlanning
            ? WrapperVariant.EstatePlanning
            : workflowProps
            ? WrapperVariant.Workflow
            : undefined
        }
      >
        <PersonalInfoMain workflowProps={workflowProps} showEstatePlanning={showEstatePlanning} />
      </Wrapper>

      {isLoading || isError ? (
        <StickyFooter />
      ) : workflowProps ? (
        <WorkflowFooter
          isSubmit
          form={FORM_ID}
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          isOnNextLoading={isUpdatingPersonalInfo || isUpdatingEstatePlanningInfo}
        />
      ) : (
        <StickyFooter fullWidthChildren maxContentWidth={800} mt={0}>
          <Button
            type="submit"
            form={FORM_ID}
            isLoading={isUpdatingPersonalInfo || isUpdatingEstatePlanningInfo}
            size="large"
            ml="auto"
            mr="16"
          >
            Update Info
          </Button>
        </StickyFooter>
      )}
    </>
  );
};

interface PersonalInfoMainProps {
  workflowProps?: WorkflowComponentProps;
  showEstatePlanning?: boolean;
  context?: PersonalInfoContext;
  onSuccess?(): void;
}

export const PersonalInfoMain: React.FC<PersonalInfoMainProps> = ({
  workflowProps,
  showEstatePlanning,
  context,
  onSuccess,
}) => {
  const { isLoading, isError, refetch, data } = usePersonalInfo({ showEstatePlanning, context });

  if (isLoading) {
    return <PersonalInfoLoading />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} mx="auto" my="56" />;
  }

  if (!data) {
    return null;
  }

  return (
    <PersonalInfoContent
      data={data}
      workflowProps={workflowProps}
      showEstatePlanning={showEstatePlanning}
      context={context}
      onSuccess={onSuccess}
    />
  );
};

export interface PersonalInfoContentProps {
  data: {
    PersonalInfo: PersonalInfoDTO;
    EstatePlanning?: EstatePlanningDTO;
    EstatePlanningOptions?: EstatePlanningOptionsDTO;
    Trusts?: TrustsDTO;
  };
  workflowProps?: WorkflowComponentProps;
  showEstatePlanning?: boolean;
  context?: PersonalInfoContext;
  onSuccess?(): void;
}

export const PersonalInfoContent: React.FC<PersonalInfoContentProps> = ({
  data,
  workflowProps,
  showEstatePlanning,
  context,
  onSuccess,
}) => {
  const [showPartnerSection, setShowPartnerSection] = useState(!!data.PersonalInfo.Partner);

  const [isDeletePartnerModalOpen, setIsDeletePartnerModalOpen] = useState(false);

  const [keepDeletedPartnerData, setKeepDeletedPartnerData] = useState<boolean | null>(null);

  const [wasWarningMessageManuallyClosed, setWarningMessageManuallyClosed] = useState(false);

  const {
    mutateAsync: updatePersonalInfo,
    error: personalInfoUpdateError,
    reset: resetPersonalInfoUpdate,
  } = usePersonalInfoUpdateMutation({ context });

  const {
    mutateAsync: updateEstatePlanningPersonalInfo,
    error: estatePlanningPersonalInfoError,
    reset: resetEstatePlanningPersonalInfo,
  } = useEstatePlanningPersonalInfoMutation();

  const formBag = useForm<FormValues>({
    defaultValues: dataToForm(data),
    resolver: yupResolver(validation),
    context: {
      showPartnerSection,
      showEstatePlanning,
      statesWithEstateTax: data?.EstatePlanningOptions?.StatesWithEstateTax || [],
    },
  });

  useEffectOnce(() => {
    const helpKey = ADV.util.setHelp(ADV.HelpDescriptors.PersonalInfoStep);

    return () => {
      ADV.util.unsetHelp(helpKey);
    };
  });

  const isFormDirty = formBag.formState.isDirty;

  useEffect(() => {
    if (isFormDirty) {
      ADV.util.Router.registerNavigateAwayCallback(showOPLConfirmationDialog);
    } else {
      ADV.util.Router.unregisterNavigateAwayCallback();
    }
  }, [isFormDirty]);

  /**
   * Due to a product requirement, the Trusts' section needs to be on the same page as the Personal
   * Info form. Since the Trusts' section and Personal Info form share the beneficiaries'
   * data we need to save the personal info form on the user's behalf before opening the Upsert
   * Trusts' modal.
   *
   * The boolean param `isUserSaving` indicates when it is in fact the user saving the form.
   * For instance, we don't want to show the success toast, or do any workflow navigation, when
   * it is not the user saving the form.
   */
  const onSubmit = (isUserSaving = true) => async (values: FormValues) => {
    const personalInfoBody = formToBodyPersonalInfo({
      showPartnerSection,
      keepDeletedPartnerData,
      values,
    });

    try {
      if (showEstatePlanning && values.deductionsAndGifts) {
        const estatePlanningBody = formToBodyEstatePlanning({
          deductionsAndGifts: values.deductionsAndGifts,
          personalInfo: personalInfoBody,
          hasPartner: showPartnerSection,
          spouseTaxFilingState: values.personalInfo.spouseTaxFilingState,
        });

        const response = await updateEstatePlanningPersonalInfo({
          body: estatePlanningBody,
        });
        formBag.reset(resetForm(response));
      } else {
        const newPersonalInfoData = await updatePersonalInfo({ body: personalInfoBody });

        formBag.reset(dataToForm({ PersonalInfo: newPersonalInfoData }));
      }

      if (isUserSaving) {
        if (workflowProps) {
          workflowProps.onNext();
        } else if (context !== PersonalInfoContext.CharitableGivingWorkflow) {
          toast.success('Your Personal Information was successfully saved.');
        }
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      {!wasWarningMessageManuallyClosed &&
        ((data.PersonalInfo.ShowPrincipalSsIncomeWarning &&
          formBag.formState.dirtyFields.personalInfo?.principal?.dob) ||
          (data.PersonalInfo.ShowSpouseSsIncomeWarning &&
            formBag.formState.dirtyFields.personalInfo?.partner?.dob)) && (
          <Alert
            message="Making changes to your date of birth will clear your previously-entered Social Security benefit information. After applying your changes, please re-enter your Social Security benefit information."
            onRequestClose={() => {
              setWarningMessageManuallyClosed(true);
            }}
            variant="info"
            mb="16"
          />
        )}

      <FormProvider {...formBag}>
        {(personalInfoUpdateError || estatePlanningPersonalInfoError) && (
          <Alert
            message={personalInfoUpdateError?.message || estatePlanningPersonalInfoError?.message}
            onRequestClose={() => {
              if (showEstatePlanning) {
                return resetEstatePlanningPersonalInfo();
              }
              return resetPersonalInfoUpdate();
            }}
            variant="error"
            mb="16"
          />
        )}

        <FormWrapper
          onSubmit={formBag.handleSubmit(onSubmit())}
          isEstatePlanning={showEstatePlanning}
          data={data}
          showPartnerSection={showPartnerSection}
          setShowPartnerSection={setShowPartnerSection}
          onAddPartner={() => setShowPartnerSection(true)}
          onDeletePartner={() => {
            // Set the values to their default instead of reset.
            // This way the "Are you sure" prompt is displayed.
            formBag.setValue('personalInfo.partner.firstName', '');
            formBag.setValue('personalInfo.partner.lastName', '');
            formBag.setValue('personalInfo.partner.dob', '');

            if (data.PersonalInfo.Partner) {
              setIsDeletePartnerModalOpen(true);
            } else {
              setShowPartnerSection(false);
            }
          }}
          submitPersonalInfo={formBag.handleSubmit(onSubmit(false))}
          resetPersonalInfo={newPersonalInfoData => {
            formBag.reset(dataToForm({ ...data, ...{ PersonalInfo: newPersonalInfoData } }));
          }}
          context={context}
        />
      </FormProvider>

      {isDeletePartnerModalOpen && (
        <DeletePartnerModal
          onDeletePartner={keepPartnerData => {
            setKeepDeletedPartnerData(keepPartnerData);
            setShowPartnerSection(false);
            setIsDeletePartnerModalOpen(false);
            formBag.setValue('personalInfo.partner.firstName', '', { shouldDirty: true });
            formBag.setValue('personalInfo.partner.lastName', '', { shouldDirty: true });
            formBag.setValue('personalInfo.partner.dob', '', { shouldDirty: true });
          }}
          onClose={() => setIsDeletePartnerModalOpen(false)}
        />
      )}
    </>
  );
};
